import { fromDatetimeLocal, toDatetimeLocalValue, validateTicketSaleDates } from '@/components/event-wizard/wizard-utils';

export type SoldOutBehavior = 'close_registration' | 'enable_waitlist' | 'hide_ticket';

export type TicketCommunicationState = {
  confirmation_email: boolean;
  update_notifications: boolean;
  whatsapp_consent: boolean;
  show_ticket_on_invoice: boolean;
  show_qr_in_confirmation: boolean;
};

export type TicketFormFieldSelection = {
  form_field_id: string;
  required: boolean;
  sort_order: number;
};

export type TicketFormState = {
  name: string;
  description: string;
  category_name: string;
  color_badge: string;
  price: number;
  original_price: number;
  sale_price: number;
  currency: string;
  visibility: 'public' | 'hidden' | 'invite_only';
  quantity: number;
  min_per_order: number;
  max_per_order: number;
  max_per_user: number;
  sold_out_behavior: SoldOutBehavior;
  approval_required: boolean;
  waitlist_enabled: boolean;
  duplicate_allowed: boolean;
  enable_hidden_ticket: boolean;
  enable_invite_only_access: boolean;
  sale_start: string;
  sale_end: string;
  refundable: boolean;
  communication: TicketCommunicationState;
  internal_notes: string;
  tags: string;
  metadata_json: string;
  event_activity_id: string;
  activity_ids: string[];
  form_fields: TicketFormFieldSelection[];
  promo_code: string;
  discount_percentage: number;
};

export type TicketFieldErrors = {
  name?: string;
  price?: string;
  original_price?: string;
  sale_price?: string;
  promo_code?: string;
  quantity?: string;
  min_per_order?: string;
  max_per_order?: string;
  max_per_user?: string;
  sale_start?: string;
  sale_end?: string;
  sold_out_behavior?: string;
  metadata_json?: string;
};

export function emptyTicketForm(): TicketFormState {
  return {
    name: 'General admission',
    description: '',
    category_name: '',
    color_badge: '',
    price: 0,
    original_price: 0,
    sale_price: 0,
    currency: 'INR',
    visibility: 'public',
    quantity: 100,
    min_per_order: 1,
    max_per_order: 5,
    max_per_user: 1,
    sold_out_behavior: 'enable_waitlist',
    approval_required: false,
    waitlist_enabled: false,
    duplicate_allowed: false,
    enable_hidden_ticket: false,
    enable_invite_only_access: false,
    sale_start: '',
    sale_end: '',
    refundable: false,
    communication: {
      confirmation_email: true,
      update_notifications: true,
      whatsapp_consent: false,
      show_ticket_on_invoice: true,
      show_qr_in_confirmation: true,
    },
    internal_notes: '',
    tags: '',
    metadata_json: '',
    event_activity_id: '',
    activity_ids: [],
    form_fields: [],
    promo_code: '',
    discount_percentage: 0,
  };
}

/** Map a saved ticket record (organizer showEvent payload) back into the wizard form for editing. */
export function ticketFormFromRecord(
  ticket: Record<string, any>,
  communication: TicketCommunicationState,
): TicketFormState {
  const base = emptyTicketForm();
  const visibility = (ticket.visibility_mode ?? ticket.visibility ?? 'public') as TicketFormState['visibility'];

  return {
    ...base,
    name: String(ticket.name ?? ''),
    description: String(ticket.description ?? ''),
    category_name: String(ticket.category?.name ?? ticket.ticket_category ?? ''),
    color_badge: String(ticket.color_badge ?? ''),
    price: Number(ticket.price ?? 0),
    original_price: Number(ticket.original_price ?? 0),
    sale_price: Number(ticket.sale_price ?? 0),
    currency: String(ticket.currency ?? 'INR'),
    visibility: ['public', 'hidden', 'invite_only'].includes(visibility) ? visibility : 'public',
    quantity: Number(ticket.inventory?.total ?? ticket.quantity_available ?? 1),
    min_per_order: Number(ticket.min_per_order ?? 1),
    max_per_order: Number(ticket.max_per_order ?? ticket.max_per_user ?? 1),
    max_per_user: Number(ticket.max_per_user ?? 1),
    sold_out_behavior: ticket.waitlist_enabled ? 'enable_waitlist' : base.sold_out_behavior,
    approval_required: Boolean(ticket.approval_required ?? ticket.approval_based),
    waitlist_enabled: Boolean(ticket.waitlist_enabled ?? ticket.waiting_list),
    duplicate_allowed: Boolean(ticket.duplicate_allowed),
    enable_hidden_ticket: visibility === 'hidden',
    enable_invite_only_access: visibility === 'invite_only',
    sale_start: toDatetimeLocalValue(ticket.sale_start),
    sale_end: toDatetimeLocalValue(ticket.sale_end),
    communication,
    event_activity_id: String(ticket.event_activity_id ?? ticket.activity?.id ?? ''),
    activity_ids: Array.isArray(ticket.activity_ids) && ticket.activity_ids.length > 0
      ? ticket.activity_ids.map((id: unknown) => String(id)).filter(Boolean)
      : Array.isArray(ticket.activities) && ticket.activities.length > 0
        ? ticket.activities.map((a: any) => String(a.id ?? '')).filter(Boolean)
        : ticket.event_activity_id || ticket.activity?.id
          ? [String(ticket.event_activity_id ?? ticket.activity?.id)]
          : [],
    form_fields: Array.isArray(ticket.form_fields)
      ? ticket.form_fields.map((f: any, i: number) => ({
          form_field_id: String(f.form_field_id ?? ''),
          required: Boolean(f.required),
          sort_order: Number(f.sort_order ?? i),
        })).filter((f: TicketFormFieldSelection) => f.form_field_id)
      : Array.isArray(ticket.form_field_mappings)
        ? ticket.form_field_mappings.map((f: any, i: number) => ({
            form_field_id: String(f.form_field_id ?? ''),
            required: Boolean(f.required),
            sort_order: Number(f.sort_order ?? i),
          })).filter((f: TicketFormFieldSelection) => f.form_field_id)
        : [],
    promo_code: String(ticket.promo_code ?? ''),
    discount_percentage: Number(ticket.discount_percentage ?? 0),
  };
}

export function communicationFromSettings(settings: Record<string, unknown> | null | undefined): TicketCommunicationState {
  const comm = (settings?.communication ?? {}) as Record<string, unknown>;
  return {
    confirmation_email: comm.confirmation_email !== false,
    update_notifications: comm.update_notifications !== false,
    whatsapp_consent: comm.whatsapp_consent === true,
    show_ticket_on_invoice: comm.show_ticket_on_invoice !== false,
    show_qr_in_confirmation: comm.show_qr_in_confirmation !== false,
  };
}

export function validateTicketForm(
  form: TicketFormState,
  registration: { registration_start?: string; registration_end?: string },
  options?: { existingNames?: string[] },
): TicketFieldErrors {
  const errors: TicketFieldErrors = {};

  const trimmedName = form.name.trim();
  if (!trimmedName) {
    errors.name = 'Ticket name is required.';
  } else if (
    (options?.existingNames ?? []).some(
      (name) => name.trim().toLowerCase() === trimmedName.toLowerCase(),
    )
  ) {
    errors.name = 'A ticket with this name already exists.';
  }
  if (!Number.isFinite(Number(form.price)) || Number(form.price) < 0) {
    errors.price = 'Price cannot be negative.';
  }
  if (Number(form.sale_price) > 0 && Number(form.sale_price) >= Number(form.price)) {
    errors.sale_price = 'Sale price must be less than the base ticket price.';
  }
  if (form.promo_code.trim() && (!Number.isInteger(Number(form.discount_percentage)) || Number(form.discount_percentage) < 1 || Number(form.discount_percentage) > 100)) {
    errors.promo_code = 'Set a discount percentage between 1 and 100.';
  }
  if (form.quantity < 1) errors.quantity = 'Quantity must be at least 1.';
  if (form.min_per_order < 1) errors.min_per_order = 'Minimum purchase must be at least 1.';
  if (form.max_per_order < form.min_per_order) {
    errors.max_per_order = 'Maximum purchase must be greater than or equal to minimum.';
  }
  if (form.max_per_user > form.max_per_order) {
    errors.max_per_user = 'Per-user limit cannot exceed maximum purchase quantity.';
  }
  if (form.max_per_user > form.quantity) {
    errors.max_per_user = 'Per-user limit cannot exceed total quantity.';
  }
  if (form.waitlist_enabled && form.sold_out_behavior === 'close_registration') {
    errors.sold_out_behavior = 'Choose waitlist or enable waitlist behavior when waitlist is on.';
  }

  const saleErrors = validateTicketSaleDates({
    sale_start: form.sale_start,
    sale_end: form.sale_end,
    registration_start: registration.registration_start,
    registration_end: registration.registration_end,
  });
  Object.assign(errors, saleErrors);

  if (form.metadata_json.trim()) {
    try {
      const parsed = JSON.parse(form.metadata_json);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        errors.metadata_json = 'Custom metadata must be a JSON object.';
      }
    } catch (err) {
      const message = err instanceof SyntaxError ? err.message : 'Invalid JSON.';
      const positionMatch = /position\s+(\d+)/i.exec(message);
      if (positionMatch) {
        const pos = Number(positionMatch[1]);
        const before = form.metadata_json.slice(0, pos);
        const line = before.split('\n').length;
        const column = (before.split('\n').pop()?.length ?? 0) + 1;
        errors.metadata_json = `Invalid JSON near line ${line}, column ${column}: ${message}`;
      } else {
        errors.metadata_json = `Invalid JSON: ${message}`;
      }
    }
  }

  return errors;
}

export function buildTicketApiPayload(form: TicketFormState): Record<string, unknown> {
  let metadata: Record<string, unknown> | undefined;
  if (form.metadata_json.trim()) {
    metadata = JSON.parse(form.metadata_json) as Record<string, unknown>;
  }

  let visibility = form.visibility;
  if (form.enable_hidden_ticket) visibility = 'hidden';
  if (form.enable_invite_only_access) visibility = 'invite_only';

  const quantity = Math.max(Number(form.quantity) || 1, 1);
  const minPerOrder = Math.max(Number(form.min_per_order) || 1, 1);
  let maxPerOrder = Math.max(Number(form.max_per_order) || minPerOrder, minPerOrder);
  let maxPerUser = Math.max(Number(form.max_per_user) || 1, 1);

  if (maxPerUser > maxPerOrder) {
    maxPerOrder = maxPerUser;
  }
  if (maxPerUser > quantity) {
    maxPerUser = quantity;
  }

  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const rawActivityId = (form.event_activity_id ?? '').trim();
  const event_activity_id = rawActivityId && UUID_PATTERN.test(rawActivityId) ? rawActivityId : null;

  const validActivityIds = (form.activity_ids ?? [])
    .map((id) => String(id ?? '').trim())
    .filter((id) => id && UUID_PATTERN.test(id));

  const validSoldOutBehaviors = ['close_registration', 'enable_waitlist', 'hide_ticket'];
  const sold_out_behavior = validSoldOutBehaviors.includes(form.sold_out_behavior)
    ? form.sold_out_behavior
    : 'close_registration';

  const payload: Record<string, unknown> = {
    name: form.name.trim() || 'Ticket',
    description: form.description.trim() || null,
    category_name: form.category_name.trim() || null,
    price: Number(form.price),
    original_price: Number(form.original_price) > 0 ? Number(form.original_price) : null,
    sale_price: Number(form.sale_price) > 0 ? Number(form.sale_price) : null,
    currency: form.currency || 'INR',
    quantity,
    min_per_order: minPerOrder,
    max_per_order: maxPerOrder,
    max_per_user: maxPerUser,
    visibility,
    approval_required: Boolean(form.approval_required),
    waitlist_enabled: Boolean(form.waitlist_enabled || sold_out_behavior === 'enable_waitlist'),
    duplicate_allowed: Boolean(form.duplicate_allowed),
    sold_out_behavior,
    color_badge: form.color_badge.trim() || null,
    internal_notes: form.internal_notes.trim() || null,
    tags: form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
    refundable: Boolean(form.refundable),
    sale_start: fromDatetimeLocal(form.sale_start),
    sale_end: fromDatetimeLocal(form.sale_end),
    event_activity_id,
    activity_ids: validActivityIds,
    apply_recommendations: false,
    promo_code: form.promo_code.trim().toUpperCase() || null,
    discount_percentage: form.promo_code.trim() ? Number(form.discount_percentage) || 0 : null,
  };

  if (metadata) {
    payload.metadata = metadata;
  }

  if (form.form_fields.length > 0) {
    const validFormFields = form.form_fields
      .filter((f) => f.form_field_id && UUID_PATTERN.test(String(f.form_field_id).trim()))
      .map((f, i) => ({
        form_field_id: String(f.form_field_id).trim(),
        required: Boolean(f.required),
        sort_order: f.sort_order ?? i,
      }));
    if (validFormFields.length > 0) {
      payload.form_fields = validFormFields;
    }
  }

  return payload;
}

export function buildCommunicationSettingsPayload(form: TicketCommunicationState): Record<string, unknown> {
  return {
    communication: {
      confirmation_email: form.confirmation_email,
      update_notifications: form.update_notifications,
      // WhatsApp consent is hidden in the wizard; always persist false for new saves.
      whatsapp_consent: false,
      show_ticket_on_invoice: form.show_ticket_on_invoice,
      show_qr_in_confirmation: form.show_qr_in_confirmation,
    },
  };
}

export function eventHasPaidTicket(tickets: Array<{ price?: number | string }>): boolean {
  return tickets.some((ticket) => Number(ticket.price) > 0);
}

export const LOCKED_PUBLISHED_TICKET_FIELDS = [] as const;

export function buildTicketPatchPayload(
  form: TicketFormState,
  originalTicket: Record<string, any>,
  isPublished: boolean = false,
): Record<string, unknown> {
  const fullPayload = buildTicketApiPayload(form);

  // Strip read-only / system fields
  delete (fullPayload as any).id;
  delete (fullPayload as any).created_at;
  delete (fullPayload as any).updated_at;
  delete (fullPayload as any).event_id;

  const diff: Record<string, unknown> = {};

  const hasChanged = (key: string, newVal: unknown): boolean => {
    switch (key) {
      case 'name':
        return String(newVal ?? '').trim() !== String(originalTicket.name ?? '').trim();
      case 'description':
        return String(newVal ?? '').trim() !== String(originalTicket.description ?? '').trim();
      case 'category_name':
        return String(newVal ?? '').trim() !== String(originalTicket.category?.name ?? originalTicket.ticket_category ?? '').trim();
      case 'color_badge':
        return String(newVal ?? '').trim() !== String(originalTicket.color_badge ?? '').trim();
      case 'price':
        return Math.abs(Number(newVal ?? 0) - Number(originalTicket.price ?? 0)) > 0.0001;
      case 'currency':
        return String(newVal ?? 'INR').trim() !== String(originalTicket.currency ?? 'INR').trim();
      case 'quantity': {
        const origQty = Number(originalTicket.inventory?.total ?? originalTicket.quantity_available ?? originalTicket.quantity ?? 0);
        return Number(newVal ?? 0) !== origQty;
      }
      case 'min_per_order':
        return Number(newVal ?? 1) !== Number(originalTicket.min_per_order ?? 1);
      case 'max_per_order':
        return Number(newVal ?? 1) !== Number(originalTicket.max_per_order ?? 1);
      case 'max_per_user':
        return Number(newVal ?? 1) !== Number(originalTicket.max_per_user ?? 1);
      case 'visibility': {
        const origVis = String(originalTicket.visibility_mode ?? originalTicket.visibility ?? 'public').trim();
        return String(newVal ?? 'public').trim() !== origVis;
      }
      case 'approval_required': {
        const origApp = Boolean(originalTicket.approval_required ?? originalTicket.approval_based);
        return Boolean(newVal) !== origApp;
      }
      case 'waitlist_enabled': {
        const origWait = Boolean(originalTicket.waitlist_enabled ?? originalTicket.waiting_list);
        return Boolean(newVal) !== origWait;
      }
      case 'duplicate_allowed':
        return Boolean(newVal) !== Boolean(originalTicket.duplicate_allowed);
      case 'sold_out_behavior':
        return String(newVal ?? '') !== String(originalTicket.rule?.eligibility_rules?.sold_out_behavior ?? originalTicket.sold_out_behavior ?? '');
      case 'internal_notes':
        return String(newVal ?? '').trim() !== String(originalTicket.internal_notes ?? '').trim();
      case 'tags': {
        const origTags = (Array.isArray(originalTicket.tags) ? originalTicket.tags : []).map(String).sort();
        const newTags = (Array.isArray(newVal) ? newVal : []).map(String).sort();
        return JSON.stringify(origTags) !== JSON.stringify(newTags);
      }
      case 'refundable':
        return Boolean(newVal) !== Boolean(originalTicket.refundable);
      case 'sale_start': {
        const origTs = originalTicket.sale_start ? new Date(originalTicket.sale_start).getTime() : null;
        const newTs = newVal ? new Date(newVal as string).getTime() : null;
        return origTs !== newTs;
      }
      case 'sale_end': {
        const origTs = originalTicket.sale_end ? new Date(originalTicket.sale_end).getTime() : null;
        const newTs = newVal ? new Date(newVal as string).getTime() : null;
        return origTs !== newTs;
      }
      case 'promo_code':
        return String(newVal ?? '').trim().toUpperCase() !== String(originalTicket.promo_code ?? '').trim().toUpperCase();
      case 'discount_percentage':
        return Number(newVal ?? 0) !== Number(originalTicket.discount_percentage ?? 0);
      case 'event_activity_id': {
        const origAct = String(originalTicket.event_activity_id ?? originalTicket.activity?.id ?? '').trim();
        return String(newVal ?? '').trim() !== origAct;
      }
      case 'activity_ids': {
        const origActIds = (Array.isArray(originalTicket.activity_ids) && originalTicket.activity_ids.length > 0
          ? originalTicket.activity_ids
          : Array.isArray(originalTicket.activities)
            ? originalTicket.activities.map((a: any) => a.id)
            : originalTicket.event_activity_id
              ? [originalTicket.event_activity_id]
              : []
        ).map(String).sort();
        const newActIds = (Array.isArray(newVal) ? (newVal as string[]) : []).map(String).sort();
        return JSON.stringify(origActIds) !== JSON.stringify(newActIds);
      }
      case 'form_fields': {
        const origF = (Array.isArray(originalTicket.form_fields)
          ? originalTicket.form_fields
          : Array.isArray(originalTicket.form_field_mappings)
            ? originalTicket.form_field_mappings
            : []
        ).map((f: any, i: number) => ({
          form_field_id: String(f.form_field_id ?? ''),
          required: Boolean(f.required),
          sort_order: Number(f.sort_order ?? i),
        }));
        return JSON.stringify(origF) !== JSON.stringify(newVal ?? []);
      }
      case 'metadata':
        return JSON.stringify(newVal ?? {}) !== JSON.stringify(originalTicket.metadata ?? {});
      default:
        return JSON.stringify(newVal) !== JSON.stringify(originalTicket[key]);
    }
  };

  const lockedSet = new Set<string>(LOCKED_PUBLISHED_TICKET_FIELDS);

  for (const [key, value] of Object.entries(fullPayload)) {
    if (isPublished && lockedSet.has(key)) {
      continue;
    }
    if (hasChanged(key, value)) {
      diff[key] = value;
    }
  }

  if (isPublished) {
    for (const locked of LOCKED_PUBLISHED_TICKET_FIELDS) {
      delete diff[locked];
    }
  }

  return diff;
}
